/**
 * What an invite's declared entity type asks of the entity selection screens (Entity Selection
 * First). The views own this mapping (ADR 62); the customer portal re-exports it. Pure, so every
 * rule is unit-tested. The screens only display it: person-api enforces it.
 */
import {
  DEFAULT_ENTITY_TYPE_GROUP_ORDER,
  ENTITY_TYPE_GROUPS,
  type TEntityChoice,
  type TEntityTypeGroupKey,
  type TSelectableEntityType,
} from '@verifime/components';
import type { TPermissionRequestType } from './entityPlan';

/** The client's onboarding workflow (`OnboardingWorkflowType`). */
export type TOnboardingWorkflowType = 'DEFAULT' | 'INDIVIDUAL_ONLY' | 'ORGANISATION_ONLY';

/**
 * The wire names of `declaredEntityType` (verifime-adr#504): one exact type, Individual included,
 * a group, or any organisation. Hand-typed: the generated spec describes the field as an open
 * string (a client must accept a value it does not know), so the portal keeps the names it acts on
 * and reads any other value as no declaration.
 */
export const DECLARED_ENTITY_TYPES = [
  'Individual',
  'Sole_Trader',
  'Partnership',
  'Private_Company',
  'Public_Company',
  'SMSF',
  'Trust_Unregulated',
  'Trust_Regulated',
  'BUSINESS',
  'COMPANY',
  'TRUST',
  'ANY_ORGANISATION',
] as const;

export type TDeclaredEntityType = (typeof DECLARED_ENTITY_TYPES)[number];

/** What a declared type asks of the screens. They only display it: person-api enforces it. */
export type TDeclaredEntityLevel =
  | { kind: 'unrestricted' }
  | { kind: 'exact'; choice: TEntityChoice }
  | { kind: 'group'; group: TEntityTypeGroupKey }
  | { kind: 'any-organisation' };

/**
 * The activation response's `declaredEntityType` when it is one of the known wire names, else
 * undefined. A wrongly cased or unknown value is no declaration: the screens stay as today and
 * the customer is never signed out for it.
 */
export function readDeclaredEntityType(value: unknown): TDeclaredEntityType | undefined {
  return DECLARED_ENTITY_TYPES.find((declared) => declared === value);
}

const UNRESTRICTED: TDeclaredEntityLevel = { kind: 'unrestricted' };

const GROUP_BY_DECLARED_TYPE: Partial<Record<TDeclaredEntityType, TEntityTypeGroupKey>> = {
  BUSINESS: 'business',
  COMPANY: 'organisation',
  TRUST: 'trust',
};

function declaredLevel(declared: TDeclaredEntityType | undefined): TDeclaredEntityLevel {
  if (!declared) {
    return UNRESTRICTED;
  }
  if (declared === 'ANY_ORGANISATION') {
    return { kind: 'any-organisation' };
  }
  const group = GROUP_BY_DECLARED_TYPE[declared];
  if (group) {
    return { kind: 'group', group };
  }
  return {
    kind: 'exact',
    choice: declared === 'Individual' ? 'individual' : (declared as TEntityChoice),
  };
}

/**
 * True when no screen could carry the declaration out under this workflow: INDIVIDUAL_ONLY has
 * only just me, ORGANISATION_ONLY never just me. person-api refuses such an invite at creation,
 * but the metadata names the code's current workflow, which can change afterwards.
 */
function conflictsWithWorkflow(
  level: TDeclaredEntityLevel,
  workflowType: TOnboardingWorkflowType,
): boolean {
  const isIndividual = level.kind === 'exact' && level.choice === 'individual';
  if (workflowType === 'INDIVIDUAL_ONLY') {
    return level.kind !== 'unrestricted' && !isIndividual;
  }
  return workflowType === 'ORGANISATION_ONLY' && isIndividual;
}

/**
 * What the declared type asks of the screens for this workflow. A conflicting declaration is
 * unrestricted: the workflow's own rules apply, as today, and the two screens cannot send the
 * customer back and forth.
 */
export function effectiveDeclaredLevel(
  declaredEntityType: unknown,
  workflowType: TOnboardingWorkflowType,
): TDeclaredEntityLevel {
  const level = declaredLevel(readDeclaredEntityType(declaredEntityType));
  return conflictsWithWorkflow(level, workflowType) ? UNRESTRICTED : level;
}

/**
 * The one group the choose screen is limited to, or undefined when the declaration does not name a
 * group. Any organisation is not a group: it limits the screen through the workflow type instead.
 * An exact type never reaches the choose screen (post-activation routing sends it to the confirm
 * screen), so the type rules it out.
 */
export function declaredGroupOf(
  level: Exclude<TDeclaredEntityLevel, { kind: 'exact' }>,
): TEntityTypeGroupKey | undefined {
  if (level.kind !== 'group') {
    return undefined;
  }
  return level.group;
}

/**
 * What a request or an invite permits the customer to share or create. The portal reads it for
 * display and for the choose list; person-api judges the confirmation and the grant.
 *
 * - `unrestricted`: Individual and every entity type.
 * - `exact`: one entity type, or Individual (`'individual'`).
 * - `group`: the entity types of one group, not Individual.
 * - `any-organisation`: every entity type, not Individual.
 */
export type TRequestCriteria =
  | { kind: 'unrestricted' }
  | { kind: 'exact'; entityType: TSelectableEntityType | 'individual' }
  | { kind: 'group'; group: TEntityTypeGroupKey }
  | { kind: 'any-organisation' };

const UNRESTRICTED_CRITERIA: TRequestCriteria = { kind: 'unrestricted' };

/**
 * The seven supported entity types, in group order (Organisation, Trust, Business). Read when
 * asked, not at import: the app imports this module for its pure helpers, and some of its tests
 * replace @verifime/components.
 */
const everyEntityType = (): readonly TSelectableEntityType[] =>
  DEFAULT_ENTITY_TYPE_GROUP_ORDER.flatMap((key) => ENTITY_TYPE_GROUPS[key].entityTypes);

const INDIVIDUAL_ENTITY_TYPES: readonly string[] = ['Individual_Domestic', 'Individual_Foreign'];

/**
 * The criteria an invite's declaration sets for its workflow.
 *
 * A known declaration the workflow allows sets them. Otherwise the workflow sets them, as
 * person-api's confirmation does, and this covers no declaration, a declaration the workflow
 * refuses and an unknown declaration (read as no declaration, as `readDeclaredEntityType` reads
 * it):
 * - DEFAULT leaves the choice open (`unrestricted`);
 * - ORGANISATION_ONLY needs an entity (`any-organisation`);
 * - INDIVIDUAL_ONLY allows no entity (Individual only).
 */
export function criteriaFromDeclaration(
  declared: string | null | undefined,
  workflowType: TOnboardingWorkflowType,
): TRequestCriteria {
  const level = effectiveDeclaredLevel(declared, workflowType);
  switch (level.kind) {
    case 'exact':
      return { kind: 'exact', entityType: level.choice };
    case 'group':
      return { kind: 'group', group: level.group };
    case 'any-organisation':
      return { kind: 'any-organisation' };
    case 'unrestricted':
      return criteriaFromWorkflow(workflowType);
  }
}

function criteriaFromWorkflow(workflowType: TOnboardingWorkflowType): TRequestCriteria {
  if (workflowType === 'ORGANISATION_ONLY') {
    return { kind: 'any-organisation' };
  }
  if (workflowType === 'INDIVIDUAL_ONLY') {
    return { kind: 'exact', entityType: 'individual' };
  }
  return UNRESTRICTED_CRITERIA;
}

/**
 * The criteria an open request sets: its own type only. A Legacy request is closed by any grant
 * for the client, so it limits nothing.
 */
export function criteriaFromRequestType(requestType: TPermissionRequestType): TRequestCriteria {
  if (requestType === 'Legacy') {
    return UNRESTRICTED_CRITERIA;
  }
  if (requestType === 'Individual') {
    return { kind: 'exact', entityType: 'individual' };
  }
  return { kind: 'exact', entityType: requestType };
}

/** The entity types the criteria permit (never Individual: see `permitsIndividual`). */
export function permittedEntityTypes(criteria: TRequestCriteria): readonly TSelectableEntityType[] {
  switch (criteria.kind) {
    case 'exact':
      return criteria.entityType === 'individual' ? [] : [criteria.entityType];
    case 'group':
      return ENTITY_TYPE_GROUPS[criteria.group].entityTypes;
    case 'unrestricted':
    case 'any-organisation':
      return everyEntityType();
  }
}

/** True when the criteria permit Individual. */
export function permitsIndividual(criteria: TRequestCriteria): boolean {
  return (
    criteria.kind === 'unrestricted' ||
    (criteria.kind === 'exact' && criteria.entityType === 'individual')
  );
}

/**
 * True when an identity of this entity type (person-api's `EntityType` as it arrives) is within the
 * criteria. Both Individual types (domestic and foreign) fit only criteria that permit Individual.
 * An entity type the portal does not offer (a foreign company, a charity) fits nothing.
 */
export function fitsCriteria(identityEntityType: string, criteria: TRequestCriteria): boolean {
  if (INDIVIDUAL_ENTITY_TYPES.includes(identityEntityType)) {
    return permitsIndividual(criteria);
  }
  return (permittedEntityTypes(criteria) as readonly string[]).includes(identityEntityType);
}
